import { APP_LOCALISATION, NUMBER_LOCALE } from "@/src_new/shared/lib/localisation";

type FormatGroupedAmountOptions = {
  locale?: string;
  roundUp?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  fallback?: string;
  allowExtractFromText?: boolean;
};

type FormatCurrencyValueOptions = FormatGroupedAmountOptions & {
  inputCurrency?: string;
  withCurrencyCode?: boolean;
  withCurrencySymbol?: boolean;
};

type FormatAmountByConfigOptions = Omit<FormatGroupedAmountOptions, "locale"> & {
  inputCurrency?: string;
  doubleDecimal?: boolean;
};

type FormatAmountOptions = {
  roundUp?: boolean;
  fallback?: string;
};

const EMBEDDED_NUMBER_PATTERN = /-?\d[\d,]*(?:\.\d+)?/;

const DEFAULT_CURRENCY_CODE =
  APP_LOCALISATION.code === "IN" ? "INR" : APP_LOCALISATION.code === "AE" ? "AED" : "USD";

const resolveAmountLocale = (inputCurrency?: string, localeOverride?: string) => {
  if (localeOverride) return localeOverride;

  return String(inputCurrency || DEFAULT_CURRENCY_CODE)
    .trim()
    .toUpperCase() === "INR"
    ? "en-IN"
    : NUMBER_LOCALE;
};

const getCurrencySymbol = (currencyCode: string) => {
  if (currencyCode === DEFAULT_CURRENCY_CODE) return APP_LOCALISATION.currencySymbol;

  return currencyCode === "INR"
    ? "₹"
    : currencyCode === "AED"
      ? "AED"
      : currencyCode === "USD"
        ? "$"
        : currencyCode;
};

export const parseAmountValue = (value: unknown, allowExtractFromText = true): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const normalizedValue = value.trim();
  if (!normalizedValue) return null;

  const directValue = Number(normalizedValue.replace(/,/g, ""));
  if (Number.isFinite(directValue)) return directValue;
  if (!allowExtractFromText) return null;

  const numberMatch = normalizedValue.match(EMBEDDED_NUMBER_PATTERN);
  if (!numberMatch) return null;

  const extractedValue = Number(numberMatch[0].replace(/,/g, ""));
  return Number.isFinite(extractedValue) ? extractedValue : null;
};

export const formatGroupedAmount = (
  value: unknown,
  options: FormatGroupedAmountOptions = {}
): string => {
  const {
    locale = resolveAmountLocale(),
    roundUp = false,
    minimumFractionDigits,
    maximumFractionDigits,
    fallback = "0",
    allowExtractFromText = true,
  } = options;

  const parsedValue = parseAmountValue(value, allowExtractFromText);
  if (parsedValue === null) return fallback;

  const normalizedValue = roundUp ? Math.ceil(parsedValue) : parsedValue;
  const hasDecimal = Math.abs(normalizedValue % 1) > Number.EPSILON;
  const minDigits = minimumFractionDigits ?? (roundUp ? 0 : hasDecimal ? 2 : 0);
  const maxDigits = maximumFractionDigits ?? (roundUp ? 0 : hasDecimal ? 2 : 0);

  return normalizedValue.toLocaleString(locale, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  });
};

export const hasEmbeddedCurrencyText = (value: unknown): boolean =>
  typeof value === "string" &&
  /[^\d\s,.-]/.test(value.trim()) &&
  EMBEDDED_NUMBER_PATTERN.test(value.trim());

export const replaceEmbeddedAmountWithGrouped = (
  value: string,
  options: Omit<FormatGroupedAmountOptions, "fallback"> = {}
) =>
  value.replace(
    EMBEDDED_NUMBER_PATTERN,
    formatGroupedAmount(value, { ...options, fallback: value })
  );

export const formatAmountByConfig = (value: unknown, options: FormatAmountByConfigOptions = {}) => {
  const {
    inputCurrency,
    doubleDecimal = false,
    minimumFractionDigits,
    maximumFractionDigits,
    fallback,
    ...rest
  } = options;

  return formatGroupedAmount(value, {
    locale: resolveAmountLocale(inputCurrency),
    minimumFractionDigits: minimumFractionDigits ?? (doubleDecimal ? 2 : undefined),
    maximumFractionDigits: maximumFractionDigits ?? (doubleDecimal ? 2 : undefined),
    fallback: fallback ?? (doubleDecimal ? "0.00" : undefined),
    ...rest,
  });
};

export const formatCoverCurrencyValue = (value: unknown, currency?: string): string => {
  if (value === null || value === undefined || value === "") return "-";

  return formatGroupedAmount(value, {
    locale: resolveAmountLocale(currency),
    fallback: String(value),
    allowExtractFromText: false,
  });
};

export const formatCurrencyValue = (
  value: unknown,
  options: FormatCurrencyValueOptions = {}
): string => {
  const {
    inputCurrency,
    withCurrencyCode = true,
    withCurrencySymbol = false,
    fallback = "0",
    minimumFractionDigits,
    maximumFractionDigits,
    locale,
    ...rest
  } = options;
  const currencyCode = String(inputCurrency || DEFAULT_CURRENCY_CODE)
    .trim()
    .toUpperCase();
  const amountLabel = formatGroupedAmount(value, {
    locale: resolveAmountLocale(currencyCode, locale),
    minimumFractionDigits,
    maximumFractionDigits,
    fallback,
    ...rest,
  });

  if (withCurrencySymbol) return `${getCurrencySymbol(currencyCode)} ${amountLabel}`;

  return withCurrencyCode ? `${currencyCode} ${amountLabel}` : amountLabel;
};

const hasValue = (value?: string | number | null) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const formatAmountValue = (value?: string | number | null): string => {
  if (!hasValue(value)) return "";

  return formatGroupedAmount(value, {
    fallback: String(value),
    allowExtractFromText: false,
  });
};

export const stripAmountValue = <T>(value: T): T | string => {
  if (typeof value !== "string") return value;

  const raw = value.replace(/,/g, "").trim();

  return Number.isFinite(Number(raw)) ? raw : value;
};

export const stripAmountFields = <T extends Record<string, unknown>>(payload: T): T => {
  const AMOUNT_FIELDS = [
    "requested_premium",
    "requested_coverage_limit",
    "requested_deductible",
    "offered_premium",
    "offered_coverage_limit",
    "offered_deductible",
  ];

  const next: Record<string, unknown> = { ...payload };

  for (const key of AMOUNT_FIELDS) {
    if (key in next) next[key] = stripAmountValue(next[key]);
  }

  return next as T;
};
